import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Truck, FileText, CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";

function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.start(); o.stop(ctx.currentTime + 0.47);
    o.onended = () => ctx.close();
  } catch { /* ignore */ }
}

function timeAgo(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const ICON = { new_request: FileText, validation: CheckCircle2, status: Truck };

export function NotificationBell({ align = "left" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const unreadRef = useRef(0);
  const navigate = useNavigate();

  const fetchN = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items); setUnread(data.unread);
      unreadRef.current = data.unread;
      const ids = new Set(data.items.map((i) => i.id));
      if (seen.current === null) {
        seen.current = ids;
      } else {
        const fresh = data.items.filter((i) => !seen.current.has(i.id) && !i.read);
        if (fresh.length > 0) {
          playBeep();
          toast(fresh[0].title, { description: fresh[0].body });
        }
        seen.current = ids;
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchN();
    const t = setInterval(fetchN, 12000);
    // Rappel sonore toutes les 60s tant qu'il reste des notifications non lues
    const reminder = setInterval(() => {
      if (unreadRef.current > 0) playBeep();
    }, 60000);
    return () => { clearInterval(t); clearInterval(reminder); };
    // eslint-disable-next-line
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); fetchN(); } catch { /* */ }
  };

  const openItem = async (n) => {
    setOpen(false);
    try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch { /* */ }
    fetchN();
    if (n.quote_id) navigate(`/app/demande/${n.quote_id}`);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell"
        className={`relative grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-slate-100 hover:text-slate-700 ${unread > 0 ? "text-rose-500" : "text-slate-500"}`}>
        {unread > 0 && (
          <span className="absolute inset-0 animate-ping rounded-lg bg-rose-400/30" />
        )}
        <Bell className={`relative h-5 w-5 ${unread > 0 ? "animate-bounce" : ""}`} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute top-11 z-50 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ${align === "right" ? "right-0" : "left-0"}`} data-testid="notif-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="font-display text-sm font-semibold">Notifications</p>
              {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs font-medium text-blue-600 hover:underline">Tout marquer lu</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-slate-400">Aucune notification.</div>
              ) : (
                items.map((n) => {
                  const Icon = ICON[n.type] || Bell;
                  return (
                    <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                      className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/40"}`}>
                      <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${n.read ? "bg-slate-100 text-slate-400" : "bg-blue-100 text-blue-600"}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold text-slate-800">{n.title}</span>
                          {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">{n.body}</span>
                        <span className="mt-1 block text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
